import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { AddWorkBar } from "@/screens/cc/AddWorkBar";
import { CaptureExtras } from "./CaptureExtras";

// Jeff 10/29: the modal used to stack TWO identical text boxes (AddWorkBar +
// the IdeaDock's own copy) plus the dock's row list — confusing and redundant.
// One box, one button, done: type or paste an idea, it lands on the board,
// Kit picks it up. The full dock (refine/convert flows) stays on the pages.
export const IdeaCapture = ({ onClose, desktop }) => {
  const closeRef = useRef(null);
  const [flash, setFlash] = useState(null);
  // Voice-memo wiring (Jeff 10/29): CaptureExtras registers its transcription
  // into the box via this setter — one form, mic fills it, no second input.
  const fillRef = useRef(null);
  const registerFill = (fn) => { fillRef.current = fn; };
  // Attachment binding (Jeff 10/30): clips/files uploaded before "Add" exist only
  // as loose rows — this getter hands their ids to the submit so they bind to the idea.
  const attachIdsRef = useRef(null);
  const registerAttachIds = (fn) => { attachIdsRef.current = fn; };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const node = (
    <div className="fixed inset-0 z-50 flex items-end justify-center font-inter sm:items-center">
      <div
        data-testid="idea-capture-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-os-ink/10 animate-in fade-in-0 duration-200 motion-reduce:animate-none"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Idea capture"
        data-testid="idea-capture-modal"
        className={cn(
          "relative z-10 flex flex-col overflow-hidden border border-os-border bg-white text-os-ink animate-in fade-in-0 duration-200 motion-reduce:animate-none",
          "w-full rounded-t-card sm:mx-4 sm:w-full sm:max-w-[42rem] sm:rounded-card",
          desktop ? "max-h-[80vh]" : "max-h-[88%] sm:max-h-[80vh]",
        )}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-os-border px-5 py-3">
          <h2 className="font-mono text-[0.8125rem] uppercase tracking-[0.08em] text-os-ink-3">
            New idea
          </h2>
          <button
            ref={closeRef}
            type="button"
            data-testid="idea-capture-close"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-control text-os-ink-3 hover:bg-os-wash hover:text-os-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink"
          >
            <X className="h-[1.125rem] w-[1.125rem]" strokeWidth={1.75} />
          </button>
        </div>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          {flash && (
            <p
              data-testid="idea-capture-flash"
              className="text-[0.875rem] font-medium text-os-green"
            >
              {flash}
            </p>
          )}
          <AddWorkBar
            registerFill={registerFill}
            registerAttachIds={registerAttachIds}
            onAdded={() =>
              setFlash("Idea captured — Kit reads it on the next pass.")
            }
          />
          <CaptureExtras
            onTranscript={(text) => fillRef.current?.(text)}
            registerAttachIds={registerAttachIds}
            onAttached={(f) =>
              setFlash(`Attached “${f.name}” — it ships with this idea.`)
            }
          />
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default IdeaCapture;
